import { useEffect, useRef } from 'react';
import { useTheme } from 'context/ThemeContext.jsx';

/**
 * The V2 background: grid, matrix rain, scanline, vignette, HUD rings.
 *
 * The rain is imperative canvas animation and is deliberately NOT rewritten in
 * React idioms — there is nothing to gain from driving a per-frame particle loop
 * through state. It is wrapped in an effect that owns its lifecycle and cancels
 * cleanly, which is all React needs to know about it.
 *
 * V1 hides all of this through [data-theme="v1"] rules in the stylesheet, so the
 * nodes are rendered for both classic themes and the cascade decides. Only the
 * animation loop is gated, because burning a rAF loop for invisible pixels is
 * waste rather than style.
 */
export default function AmbientLayers() {
  const canvasRef = useRef(null);
  const { theme } = useTheme();
  const active = theme === 'v2';

  useEffect(() => {
    if (!active) return undefined;
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    if (window.matchMedia('(prefers-reduced-motion:reduce)').matches) return undefined;

    const ctx = canvas.getContext('2d');
    let raf = 0;
    let columns = [];

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      columns = new Array(Math.ceil(canvas.width / 14)).fill(0).map(() => Math.random() * canvas.height);
    };
    resize();
    window.addEventListener('resize', resize);

    const GLYPHS = 'アイウエオカキクケコ0123456789ABCDEF';
    let last = 0;
    const draw = ts => {
      raf = requestAnimationFrame(draw);
      if (ts - last < 50) return; // ~20fps is plenty for falling glyphs
      last = ts;

      ctx.fillStyle = 'rgba(2,6,12,0.08)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = 'rgba(0,255,156,0.5)';
      ctx.font = '12px monospace';

      for (let i = 0; i < columns.length; i++) {
        const ch = GLYPHS[Math.floor(Math.random() * GLYPHS.length)];
        ctx.fillText(ch, i * 14, columns[i]);
        columns[i] = columns[i] > canvas.height && Math.random() > 0.975 ? 0 : columns[i] + 14;
      }
    };
    raf = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
    };
  }, [active]);

  return (
    <>
      <div id="hax-grid" />
      <canvas id="hax-rain" ref={canvasRef} />
      <div id="hud-rings">
        <svg viewBox="0 0 900 900" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g className="hud-ring-a">
            <circle cx="450" cy="450" r="420" stroke="#22d3ee" strokeWidth="0.5" strokeDasharray="8 16" />
            <circle cx="450" cy="450" r="380" stroke="#22d3ee" strokeWidth="0.3" strokeDasharray="3 24" />
            <line x1="450" y1="30" x2="450" y2="50" stroke="#22d3ee" strokeWidth="1" />
            <line x1="450" y1="850" x2="450" y2="870" stroke="#22d3ee" strokeWidth="1" />
            <line x1="30" y1="450" x2="50" y2="450" stroke="#22d3ee" strokeWidth="1" />
            <line x1="850" y1="450" x2="870" y2="450" stroke="#22d3ee" strokeWidth="1" />
          </g>
          <g className="hud-ring-b">
            <circle cx="450" cy="450" r="300" stroke="#38bdf8" strokeWidth="0.4" strokeDasharray="12 20" />
            <circle cx="450" cy="450" r="260" stroke="#38bdf8" strokeWidth="0.3" strokeDasharray="2 18" />
            <circle cx="450" cy="450" r="4" fill="#22d3ee" opacity="0.6" />
          </g>
          <g className="hud-ring-c">
            <circle cx="450" cy="450" r="190" stroke="#22d3ee" strokeWidth="0.3" strokeDasharray="6 12" />
            <circle cx="450" cy="450" r="140" stroke="#38bdf8" strokeWidth="0.25" strokeDasharray="2 10" />
          </g>
        </svg>
      </div>
      <div id="hax-scanline" />
      <div id="hax-vignette" />
    </>
  );
}
